import React from 'react';

import {
	AI_SECTION,
	AI_SECTION_COMMENT_PRIMARY_TOOLBAR,
	AI_VIEW_SECTION,
	HERO_PROMPT_BUTTON_GROUP,
	ROVO_PROMPT_MENU_SECTION,
	ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL,
	TOOLBARS,
} from '@atlaskit/editor-common/toolbar/keys';
import { AI_SECTION_RANK } from '@atlaskit/editor-common/toolbar/rank';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { RemixContentComponentDefinition } from '@atlassian/malleable-ui-components/types';

import type {
	MalleableUiPlugin,
	RemixDisabledReason,
	RemixHiddenOptionsResolver,
} from '../../malleableUiPluginType';
import { RemixButton } from '../components/RemixButton';
import { RemixCommentPrimaryMenuItem } from '../components/RemixCommentPrimaryMenuItem';

const MAUI_REMIX_GROUP = {
	type: 'group' as const,
	key: 'malleable-ui-remix-group',
};

const MAUI_REMIX_BUTTON = {
	type: 'button' as const,
	key: 'malleable-ui-remix-button',
};

const MAUI_REMIX_COMMENT_MENU_ITEM = {
	type: 'menu-item' as const,
	key: 'malleable-ui-remix-comment-menu-item',
};

/**
 * Between {@link ROVO_PROMPT_MENU_SECTION_RANK}[SPELLING_AND_GRAMMAR_MENU_ITEM] (300) and
 * [CHANGE_TONE_NESTED_MENU] (400); see `packages/editor/editor-common/src/toolbar/rank.ts`.
 */
const MAUI_REMIX_COMMENT_MENU_ITEM_RANK = 350;

function isCommentPrimaryToolbarContext(parents: { key: string }[]): boolean {
	return parents.some((p) => p.key === AI_SECTION_COMMENT_PRIMARY_TOOLBAR.key);
}

export const getSelectionToolbarComponents: (
	api: ExtractInjectionAPI<MalleableUiPlugin>,
	options?: {
		isDisabled?: boolean;
		isRemixHidden?: boolean;
		remixDisabledReason?: RemixDisabledReason;
		remixHiddenOptionsResolver?: RemixHiddenOptionsResolver;
		isSelectionRemixable?: RemixContentComponentDefinition['isSelectionRemixable'];
	},
) => RegisterComponent[] = (api, options) => {
	return [
		{
			type: MAUI_REMIX_GROUP.type,
			key: MAUI_REMIX_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION.key,
					rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key] + 10,
				},
				// View mode (Live Page): AI_VIEW_SECTION
				{
					type: AI_VIEW_SECTION.type,
					key: AI_VIEW_SECTION.key,
					rank: AI_SECTION_RANK[HERO_PROMPT_BUTTON_GROUP.key] + 10,
				},
			],
		},
		{
			type: MAUI_REMIX_BUTTON.type,
			key: MAUI_REMIX_BUTTON.key,
			parents: [
				{
					type: MAUI_REMIX_GROUP.type,
					key: MAUI_REMIX_GROUP.key,
					rank: 100,
				},
			],
			component: ({ parents }) => {
				const onPrimaryToolbar = parents.at(0)?.key === TOOLBARS.PRIMARY_TOOLBAR;
				if (onPrimaryToolbar) {
					return null;
				}
				return (
					<RemixButton
						api={api}
						isDisabled={options?.isDisabled}
						isRemixHidden={options?.isRemixHidden}
						remixDisabledReason={options?.remixDisabledReason}
						remixHiddenOptionsResolver={options?.remixHiddenOptionsResolver}
						isSelectionRemixable={options?.isSelectionRemixable}
					/>
				);
			},
		},
		{
			type: MAUI_REMIX_COMMENT_MENU_ITEM.type,
			key: MAUI_REMIX_COMMENT_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: MAUI_REMIX_COMMENT_MENU_ITEM_RANK,
				},
				{
					type: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.type,
					key: ROVO_PROMPT_MENU_SECTION_DISPLAY_SMALL.key,
					rank: MAUI_REMIX_COMMENT_MENU_ITEM_RANK,
				},
			],
			component: ({ parents }) => {
				if (
					!isCommentPrimaryToolbarContext(parents) ||
					options?.isRemixHidden ||
					!fg('platform_editor_maui_jira_updates')
				) {
					return null;
				}
				return (
					<RemixCommentPrimaryMenuItem
						api={api}
						isDisabled={options?.isDisabled}
						remixDisabledReason={options?.remixDisabledReason}
						isSelectionRemixable={options?.isSelectionRemixable}
					/>
				);
			},
		},
	];
};
